import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

export type PillSize = 'xs' | 'sm' | 'md' | 'lg';
export type PillVariant = 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'indigo' | 'gray' | 'cyan' | 'orange' | 'pink';

/**
 * Pill Component - Rounded pill/tag for counts, statuses, labels
 *
 * @example
 * <!-- Count pill -->
 * <app-atom-pill variant="green">4 Active</app-atom-pill>
 *
 * <!-- Small gray pill for tab counts -->
 * <app-atom-pill variant="gray" size="xs">12</app-atom-pill>
 *
 * <!-- Status pill -->
 * <app-atom-pill variant="blue" size="sm">Ongoing</app-atom-pill>
 */
@Component({
  selector: 'app-atom-pill',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span [class]="pillClasses()">
      <ng-content></ng-content>
    </span>
  `,
  styles: [`
    :host {
      display: inline-flex;
    }
  `]
})
export class PillComponent {
  /** Color variant */
  variant = input<PillVariant>('gray');
  
  /** Size of the pill */
  size = input<PillSize>('sm');
  
  /** Whether to use filled (solid) or soft (light) style */
  filled = input<boolean>(false);
  
  /** Additional CSS classes */
  additionalClasses = input<string>('');

  /** Size to padding/text mapping */
  private sizeMap: Record<PillSize, string> = {
    'xs': 'px-1.5 py-0.5 text-xs',
    'sm': 'px-2 py-0.5 text-xs',
    'md': 'px-2.5 py-1 text-sm',
    'lg': 'px-3 py-1.5 text-sm'
  };

  /** Variant to soft (light bg) color mapping */
  private softColorMap: Record<PillVariant, string> = {
    'blue': 'bg-blue-100 text-blue-700',
    'green': 'bg-green-100 text-green-700',
    'red': 'bg-red-100 text-red-700',
    'yellow': 'bg-yellow-100 text-yellow-700',
    'purple': 'bg-purple-100 text-purple-700',
    'indigo': 'bg-indigo-100 text-indigo-700',
    'gray': 'bg-gray-100 text-gray-600',
    'cyan': 'bg-cyan-100 text-cyan-700',
    'orange': 'bg-orange-100 text-orange-700',
    'pink': 'bg-pink-100 text-pink-700'
  };

  /** Variant to filled (solid bg) color mapping */
  private filledColorMap: Record<PillVariant, string> = {
    'blue': 'bg-blue-600 text-white',
    'green': 'bg-green-600 text-white',
    'red': 'bg-red-600 text-white',
    'yellow': 'bg-yellow-500 text-white',
    'purple': 'bg-purple-600 text-white',
    'indigo': 'bg-indigo-600 text-white',
    'gray': 'bg-gray-600 text-white',
    'cyan': 'bg-cyan-600 text-white',
    'orange': 'bg-orange-600 text-white',
    'pink': 'bg-pink-600 text-white'
  };

  /** Computed pill classes */
  pillClasses = computed(() => {
    const colorMap = this.filled() ? this.filledColorMap : this.softColorMap;
    
    const classes = [
      'inline-flex',
      'items-center',
      'font-medium',
      'rounded-full',
      this.sizeMap[this.size()],
      colorMap[this.variant()]
    ];
    
    if (this.additionalClasses()) {
      classes.push(this.additionalClasses());
    }
    
    return classes.join(' ');
  });
}
